import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Services", to: "/#services" },
  { label: "Listen", to: "/#ab-player" },
  { label: "Gear", to: "/#studio" },
  { label: "Shop", to: "/shop" },
  { label: "Contact", to: "/#contact" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const hash = to.slice(1);
      if (location.pathname !== "/") {
        navigate("/" + hash);
      } else {
        // Motion-blur the main content briefly while we smooth-scroll
        document.body.classList.add("is-scroll-blurring");
        const el = document.querySelector(hash);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.setTimeout(() => {
          document.body.classList.remove("is-scroll-blurring");
        }, 650);
      }
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-50 max-w-[1920px] mx-auto transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 h-20 md:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="Infinagon Recordings — Home"
          className="flex items-center h-full group"
        >
          <img
            src="/assets/logo-black.png"
            alt="Infinagon Recordings"
            className="h-full w-auto max-h-16 md:max-h-20 object-contain"
            style={{ aspectRatio: "auto" }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={(e) => handleClick(e, l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="font-script text-xl text-black hover:text-black/50 transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <a
            href="https://engineears.com/infinagon"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-btn"
            className="font-mono text-[11px] tracking-[0.25em] uppercase bg-black text-white px-5 py-3 hover:bg-white hover:text-black border-2 border-black transition-colors"
          >
            Book Session
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 border-2 border-black"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-white border-t border-black/15 px-6 py-6 flex flex-col gap-5"
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={(e) => handleClick(e, l.to)}
              className="font-script text-2xl"
            >
              {l.label}
            </Link>
          ))}
          <a
            href="https://engineears.com/infinagon"
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs tracking-[0.25em] uppercase bg-black text-white px-5 py-3 inline-block text-center border-2 border-black"
          >
            Book Session
          </a>
        </div>
      )}
    </header>
  );
};

export default Navigation;
